import { createContext, useContext, type ReactNode } from 'react'
import { mayRead, type ReadKey } from '../lib/adminReads'
import type { Identity } from '../lib/types'
import type { OrgContext } from './OrgSwitch'

// Who is signed in, and the two things the top bar does with it (go to the main
// menu, sign out), handed down once so a page does not thread them to its bar.
export interface SessionValue {
  identity: Identity
  navigate: (to: string) => void
  signOut: () => void
  /** Ask /auth/me again, after a change that alters what the session carries (options). */
  refresh: () => Promise<void>
  /** The installation owner's chosen organisation (ui/OrgSwitch). */
  org?: OrgContext
}

const SessionContext = createContext<SessionValue | null>(null)

export function SessionProvider({ value, children }: { value: SessionValue | null; children: ReactNode }) {
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

/** The signed-in session, or null outside one (sign-in, a machine's page). */
export function useSession(): SessionValue | null {
  return useContext(SessionContext)
}

/** Whether the signed-in person may make this read (lib/adminReads); true outside a session. */
export function useMayRead(read: ReadKey): boolean {
  const session = useSession()
  return !session || mayRead(session.identity.permissions, read)
}
